import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MeResponse, PublicUser } from '../../shared/protocol.ts';
import { AuthContext, fetchMe, type AuthContextValue } from './auth.ts';
import { localizeHref } from './router.ts';

/** Offline or server down: behave as signed out, with no sign-in button. */
function loadMe(): Promise<MeResponse> {
  return fetchMe().catch(() => ({ user: null, login: { google: false, dev: false } }));
}

/** Loads who is signed in once, and offers sign-in / sign-out to the whole app. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);

  const refresh = useCallback(() => loadMe().then(setMe), []);

  useEffect(() => {
    let alive = true;
    loadMe().then((value) => {
      if (alive) setMe(value);
    });
    return () => {
      alive = false;
    };
  }, []);

  const setUser = useCallback((user: PublicUser) => {
    setMe((prev) => (prev ? { ...prev, user } : prev));
  }, []);

  const signIn = useCallback(
    (returnTo = window.location.pathname) => {
      if (!me) return;
      const target = me.login.google ? '/api/auth/google/start' : me.login.dev ? '/api/auth/dev-login' : null;
      if (target) window.location.href = `${target}?return=${encodeURIComponent(localizeHref(returnTo))}`;
    },
    [me],
  );

  const signOut = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setMe((prev) => (prev ? { ...prev, user: null } : prev));
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const user = me?.user ?? null;
    return {
      me,
      user,
      needsUsername: Boolean(user && !user.username),
      canSignIn: Boolean(me && (me.login.google || me.login.dev)),
      refresh,
      setUser,
      signIn,
      signOut,
    };
  }, [me, refresh, setUser, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
